'use client';

import { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type PlanId = 'free' | 'plus';

export interface PlanLimits {
  monthlyMessages: number | null; // null = unlimited
  journalEntries: number | null;  // null = unlimited
  guidedSessions: number | null;  // null = unlimited
  voiceInput: boolean;
  weeklyReflections: boolean;
  sessionSummaries: boolean;
  ragResponses: boolean;
  moodHistory: boolean;
}

const PLAN_LIMITS: Record<PlanId, PlanLimits> = {
  free: {
    monthlyMessages: 20,
    journalEntries: 10,
    guidedSessions: 3,
    voiceInput: false,
    weeklyReflections: false,
    sessionSummaries: false,
    ragResponses: false,
    moodHistory: true,
  },
  plus: {
    monthlyMessages: null,
    journalEntries: null,
    guidedSessions: null,
    voiceInput: true,
    weeklyReflections: true,
    sessionSummaries: true,
    ragResponses: true,
    moodHistory: true,
  },
};

export interface SubscriptionState {
  plan: PlanId;
  status: string | null;
  isExpired: boolean;
  isPaused: boolean;
  limits: PlanLimits;
  loading: boolean;
  // Usage counts
  monthlyMessageCount: number;
  journalEntryCount: number;
  guidedSessionCount: number;
  // Derived helpers
  canSendMessage: boolean;
  canAddJournalEntry: boolean;
  canStartGuidedSession: boolean;
  refreshUsage: () => void;
}

export function useSubscription(): SubscriptionState {
  const { user } = useAuth();
  const [plan, setPlan] = useState<PlanId>('free');
  const [status, setStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [monthlyMessageCount, setMonthlyMessageCount] = useState(0);
  const [journalEntryCount, setJournalEntryCount] = useState(0);
  const [guidedSessionCount, setGuidedSessionCount] = useState(0);
  const supabase = createClient();

  const fetchSubscription = useCallback(async () => {
    if (!user) {
      setPlan('free');
      setStatus(null);
      setLoading(false);
      return;
    }

    try {
      const { data } = await supabase
        .from('user_subscriptions')
        .select('plan, status')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (data?.plan && ['free', 'plus'].includes(data.plan)) {
        setPlan(data.plan as PlanId);
        setStatus(data.status);
      } else {
        setPlan('free');
        setStatus(null);
      }
    } catch {
      setPlan('free');
      setStatus(null);
    }
  }, [user]);

  const fetchUsage = useCallback(async () => {
    if (!user) {
      setMonthlyMessageCount(0);
      setJournalEntryCount(0);
      setGuidedSessionCount(0);
      setLoading(false);
      return;
    }

    try {
      // Monthly message count (current calendar month, user messages only)
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const [msgResult, journalResult, sessionResult] = await Promise.all([
        supabase
          .from('chat_messages')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('role', 'user')
          .gte('created_at', startOfMonth.toISOString()),
        supabase
          .from('journal_entries')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id),
        supabase
          .from('guided_session_responses')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id),
      ]);

      setMonthlyMessageCount(msgResult.count ?? 0);
      setJournalEntryCount(journalResult.count ?? 0);
      setGuidedSessionCount(sessionResult.count ?? 0);
    } catch {
      // keep existing counts
    } finally {
      setLoading(false);
    }
  }, [user]);

  const refreshUsage = useCallback(() => {
    fetchUsage();
  }, [fetchUsage]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchSubscription(), fetchUsage()]);
  }, [user]);

  const limits = PLAN_LIMITS[plan];

  // When the subscription is expired every feature gate is closed.
  const isExpired = status === 'expired';
  // Paused subscriptions retain access to features (user paid, just paused billing)
  const isPaused = status === 'paused';

  const canSendMessage =
    !isExpired &&
    (limits.monthlyMessages === null || monthlyMessageCount < limits.monthlyMessages);

  const canAddJournalEntry =
    !isExpired &&
    (limits.journalEntries === null || journalEntryCount < limits.journalEntries);

  const canStartGuidedSession =
    !isExpired &&
    (limits.guidedSessions === null || guidedSessionCount < limits.guidedSessions);

  return {
    plan,
    status,
    isExpired,
    isPaused,
    limits: isExpired
      ? {
          monthlyMessages: 0,
          journalEntries: 0,
          guidedSessions: 0,
          voiceInput: false,
          weeklyReflections: false,
          sessionSummaries: false,
          ragResponses: false,
          moodHistory: false,
        }
      : limits,
    loading,
    monthlyMessageCount,
    journalEntryCount,
    guidedSessionCount,
    canSendMessage,
    canAddJournalEntry,
    canStartGuidedSession,
    refreshUsage,
  };
}
